import React, { useLayoutEffect, useRef, useState } from 'react';
import { Button, Input, Select, Table } from 'antd';
import { DownloadOutlined, PlusOutlined, SyncOutlined } from '@ant-design/icons';
import type { TableProps } from 'antd';
import { useSkillStudioLayout } from './SkillStudio';

const { Search } = Input;

const STANDALONE_SECTION_HEIGHT = 'calc(100vh - 160px)';
const MIN_TABLE_BODY_HEIGHT = 120;

export interface SkillStudioOption {
    value: string;
    label: React.ReactNode;
}

export interface SkillStudioGroundTruthSectionProps<T extends object = Record<string, unknown>> {
    filterSets?: string[];
    filterStatus?: string;
    questionSetOptions: SkillStudioOption[];
    statusOptions: SkillStudioOption[];
    onFilterSetsChange: (value: string[]) => void;
    onFilterStatusChange: (value?: string) => void;
    onSearch: (value: string) => void;
    onSearchInputChange?: (value: string) => void;
    selectedRowCount: number;
    refreshing: boolean;
    onRefreshData?: () => void;
    onRegenerateAgent?: () => void;
    onExport: () => void;
    onCreate: () => void;
    tableProps: TableProps<T>;
    footerContent?: React.ReactNode;
}

const SkillStudioGroundTruthSection = <T extends object = Record<string, unknown>>({
    filterSets = [],
    filterStatus,
    questionSetOptions,
    statusOptions,
    onFilterSetsChange,
    onFilterStatusChange,
    onSearch,
    onSearchInputChange,
    selectedRowCount,
    refreshing,
    onRefreshData,
    onRegenerateAgent,
    onExport,
    onCreate,
    tableProps,
    footerContent,
}: SkillStudioGroundTruthSectionProps<T>) => {
    const skillStudioLayout = useSkillStudioLayout();
    const sectionHeight = skillStudioLayout != null ? '100%' : STANDALONE_SECTION_HEIGHT;

    const tableWrapRef = useRef<HTMLDivElement>(null);
    const [bodyScrollY, setBodyScrollY] = useState<number>();
    const [questionSetsOpen, setQuestionSetsOpen] = useState(false);
    const [draftFilterSets, setDraftFilterSets] = useState<string[]>(filterSets);

    useLayoutEffect(() => {
        if (!questionSetsOpen) {
            setDraftFilterSets(filterSets);
        }
    }, [filterSets, questionSetsOpen]);

    const paginationCurrent =
        typeof tableProps.pagination === 'object' && tableProps.pagination !== null
            ? tableProps.pagination.current
            : undefined;
    const dataSourceLength = Array.isArray(tableProps.dataSource)
        ? tableProps.dataSource.length
        : 0;

    useLayoutEffect(() => {
        const wrap = tableWrapRef.current;
        if (!wrap) return;

        const updateBodyScrollY = () => {
            const thead = wrap.querySelector('.ant-table-thead');
            const pagination = wrap.querySelector('.ant-table-pagination');
            const theadHeight = thead?.getBoundingClientRect().height ?? 0;
            const paginationHeight = pagination?.getBoundingClientRect().height ?? 0;
            const available = wrap.clientHeight - theadHeight - paginationHeight;
            setBodyScrollY(Math.max(available, MIN_TABLE_BODY_HEIGHT));
        };

        updateBodyScrollY();
        const observer = new ResizeObserver(updateBodyScrollY);
        observer.observe(wrap);
        window.addEventListener('resize', updateBodyScrollY);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateBodyScrollY);
        };
    }, [tableProps.loading, dataSourceLength, paginationCurrent]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: sectionHeight,
                minHeight: 0,
                paddingBottom: 24,
            }}
        >
            <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Question Set"
                    style={{ minWidth: 280, flex: '1 1 280px', maxWidth: 560 }}
                    value={draftFilterSets}
                    open={questionSetsOpen}
                    onDropdownVisibleChange={(visible) => {
                        setQuestionSetsOpen(visible);
                        if (!visible) {
                            // Commit when the menu closes so multi-pick isn't interrupted by table reloads.
                            onFilterSetsChange(draftFilterSets);
                        }
                    }}
                    onChange={(values) => {
                        const next = values ?? [];
                        setDraftFilterSets(next);
                        if (questionSetsOpen) {
                            // Keep the menu open for additional selections.
                            setQuestionSetsOpen(true);
                            return;
                        }
                        // Clear / tag-remove while closed should apply immediately.
                        onFilterSetsChange(next);
                    }}
                    options={questionSetOptions}
                    maxTagCount="responsive"
                    tokenSeparators={[',']}
                    getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
                />
                <Select
                    allowClear
                    placeholder="Status"
                    style={{ width: 140 }}
                    value={filterStatus}
                    onChange={onFilterStatusChange}
                    options={statusOptions}
                />
                <Search
                    placeholder="Search questions..."
                    allowClear
                    style={{ width: 260 }}
                    onSearch={onSearch}
                    onChange={(e) => onSearchInputChange?.(e.target.value)}
                />
                <div style={{ flex: 1 }} />
                {onRefreshData && (
                    <Button
                        icon={<SyncOutlined spin={refreshing} />}
                        disabled={selectedRowCount === 0 || refreshing}
                        onClick={onRefreshData}
                    >
                        Refresh Data{selectedRowCount > 0 ? ` (${selectedRowCount})` : ''}
                    </Button>
                )}
                {onRegenerateAgent && (
                    <Button
                        icon={<SyncOutlined spin={refreshing} />}
                        disabled={selectedRowCount === 0 || refreshing}
                        onClick={onRegenerateAgent}
                    >
                        Regenerate Agent{selectedRowCount > 0 ? ` (${selectedRowCount})` : ''}
                    </Button>
                )}
                <Button icon={<DownloadOutlined />} onClick={onExport}>
                    Export
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}>
                    New Ground Truth
                </Button>
            </div>

            <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0 }}>
                <Table
                    {...tableProps}
                    scroll={{
                        ...tableProps.scroll,
                        ...(bodyScrollY != null ? { y: bodyScrollY } : {}),
                    }}
                />
            </div>
            {footerContent}
        </div>
    );
};

export default SkillStudioGroundTruthSection;
